import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import AuditActionChip from './AuditActionChip';
import { formatDate } from '../../../../utils/Utils';

const isPresent = (value) => value !== undefined && value !== null;

/**
 * Deriva um resumo de 1 linha a partir do JSON de `details`, priorizando os campos mais
 * comuns (name, ip, from/to, is_test); sem esses, mostra as primeiras chaves como "k: v".
 * @param {Object|null} details
 * @returns {string}
 */
function summarizeDetails(details) {
  if (!details || typeof details !== 'object') return '—';
  if (details.name) return String(details.name);
  if (details.ip) return String(details.ip);
  if (isPresent(details.from) && isPresent(details.to)) return `${details.from} → ${details.to}`;
  if (isPresent(details.is_test)) return `is_test: ${details.is_test ? 'sim' : 'não'}`;

  const entries = Object.entries(details).slice(0, 2);
  if (entries.length === 0) return '—';
  return entries
    .map(([key, value]) => `${key}: ${typeof value === 'object' ? JSON.stringify(value) : value}`)
    .join(', ');
}

/**
 * Formata 'YYYY-MM-DD HH:MM:SS' (fuso do servidor) com formatDate() do projeto.
 * @param {string} dateStr
 * @returns {string}
 */
function formatCreatedAt(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return formatDate(date);
}

/**
 * AuditRow - Linha da tabela de auditoria; clique expande o JSON de `details` (Admin Geral > Auditoria)
 * @param {Object} props
 * @param {Object} props.entry - id, admin_email, company_id, company_name, action, details, details_raw, ip_address, created_at
 */
function AuditRow({ entry }) {
  const [expanded, setExpanded] = useState(false);
  const { admin_email: adminEmail, company_id: companyId, company_name: companyName, action, details, details_raw: detailsRaw, ip_address: ipAddress, created_at: createdAt } = entry;

  let prettyDetails = null;
  if (details && typeof details === 'object') {
    prettyDetails = JSON.stringify(details, null, 2);
  } else if (detailsRaw) {
    prettyDetails = detailsRaw;
  }

  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => setExpanded((prev) => !prev)}>
        <TableCell>
          <Typography variant="body2">{formatCreatedAt(createdAt)}</Typography>
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary">{adminEmail || '—'}</Typography>
        </TableCell>

        <TableCell><AuditActionChip action={action} /></TableCell>

        <TableCell onClick={(e) => e.stopPropagation()}>
          {isPresent(companyId) ? (
            <Link component={RouterLink} to={`/admin/companies/${companyId}`} sx={{ fontSize: '0.875rem' }}>
              {companyName || `Empresa #${companyId}`}
            </Link>
          ) : (
            <Typography variant="body2" color="text.disabled">—</Typography>
          )}
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '0.8125rem' }}>
            {ipAddress || '—'}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 320 }}>
            {summarizeDetails(details)}
          </Typography>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, border: expanded ? undefined : 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: '#F8F9FC' }}>
              {prettyDetails ? (
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    bgcolor: '#0B0F14',
                    color: '#D6E2EF',
                    borderRadius: '8px',
                    fontSize: '0.75rem',
                    fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {prettyDetails}
                </Box>
              ) : (
                <Typography variant="body2" color="text.disabled">Sem detalhes registrados</Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default AuditRow;
